/**
 * Layer 2 — static map features, built once per map:
 *  - rivers: Chaikin-smoothed `map.rivers` polylines, width growing downstream
 *  - roads:  hex-centre to hex-centre, curved through edge midpoints; cased main roads,
 *            dashed tracks
 *  - rails:  dark line with ties
 *  - forest trees and mountain peaks: instanced atlas glyphs placed with jitter, kept inland of
 *    the organic coast (sampled from the baked coast distance field)
 */
import { type GameMap, HEX_SIZE, HEX_W, type HexGrid, Terrain } from '@friction/engine';
import { Container } from 'pixi.js';
import { type Glyph, peakGlyphs, treeGlyphs } from '../atlas/glyphs';
import { sampleField, type TerrainFields } from '../bake/terrainFields';
import { chaikin, curveThroughMidpoints, linkChains } from '../geom/paths';
import type { Atlas } from '../gfx/atlas';
import { LineBatch } from '../gfx/lines';
import { QuadBatch, Scale } from '../gfx/quads';
import { PAL } from '../palette';

const hash = (a: number, b: number): number => {
  let h = Math.imul(a ^ 0x9e3779b9, 0x85ebca6b) ^ Math.imul(b + 0x632be5ab, 0xc2b2ae35);
  h = Math.imul(h ^ (h >>> 15), 0x2c1b3c6d);
  return ((h ^ (h >>> 13)) >>> 0) / 4294967296;
};

export class FeaturesLayer {
  readonly container = new Container({ label: 'features' });
  private readonly rivers = new LineBatch('rivers', 8192);
  private readonly roads = new LineBatch('roads', 8192);
  private readonly rails = new LineBatch('rails', 4096);
  private readonly glyphs: QuadBatch;

  constructor(map: GameMap, grid: HexGrid, fields: TerrainFields, atlas: Atlas) {
    this.glyphs = new QuadBatch('terrain-glyphs', atlas.texture, 8192);
    this.buildRivers(map);
    this.buildRoads(map, grid);
    this.buildGlyphs(map, grid, fields, atlas);
    this.container.addChild(this.rivers.mesh, this.roads.mesh, this.rails.mesh, this.glyphs.mesh);
  }

  private buildRivers(map: GameMap): void {
    const b = this.rivers;
    for (const river of map.rivers) {
      if (river.points.length < 4) continue;
      const { pts, values } = chaikin(river.points, 3, false, river.widths);
      const widths = (values ?? []).map((w) => 1.1 + w * 1.25);
      // taper the source to a point
      if (widths.length > 2) widths[0] *= 0.3;
      b.add(pts, { width: 2, color: PAL.river, alpha: 0.95, minPx: 0.9, maxPx: 9 }, { widths });
    }
    b.commit();
  }

  private buildRoads(map: GameMap, grid: HexGrid): void {
    const isCity = (h: number) => map.cityAt[h] >= 0;
    const jitter = (h: number): [number, number] => [(hash(h, 3) - 0.5) * 7, (hash(h, 5) - 0.5) * 7];
    const major = (chain: number[]) => {
      if (chain.some((h) => map.rails[h] !== 0)) return true;
      const end = (h: number) => map.cityAt[h] >= 0 && map.cities[map.cityAt[h]].size >= 2;
      return end(chain[0]) && end(chain[chain.length - 1]);
    };
    const roadChains = linkChains(grid, map.roads, isCity);
    const r = this.roads;
    const casings: number[][] = [];
    const fills: number[][] = [];
    const tracks: number[][] = [];
    for (const chain of roadChains) {
      const pts = curveThroughMidpoints(grid, chain, jitter);
      if (major(chain)) {
        casings.push(pts);
        fills.push(pts);
      } else tracks.push(pts);
    }
    for (const p of tracks)
      r.add(p, { width: 1.6, color: PAL.road, alpha: 0.85, minPx: 0.8, maxPx: 3.2, dash: 6, gap: 3.5 });
    for (const p of casings) r.add(p, { width: 3.6, color: PAL.road, alpha: 0.95, minPx: 1.4, maxPx: 6.5 });
    for (const p of fills) r.add(p, { width: 1.9, color: PAL.roadFill, alpha: 1, minPx: 0.6, maxPx: 3.6 });
    r.commit();

    const railChains = linkChains(grid, map.rails, isCity);
    const rl = this.rails;
    for (const chain of railChains) {
      const pts = curveThroughMidpoints(grid, chain);
      rl.add(pts, {
        width: 5,
        color: PAL.rail,
        alpha: 0.9,
        minPx: 3,
        maxPx: 7,
        dash: 0.9,
        gap: 4.2,
        offsetPx: 0,
      });
      rl.add(pts, { width: 1.5, color: PAL.rail, alpha: 1, minPx: 1, maxPx: 2.6 });
    }
    rl.commit();
  }

  private buildGlyphs(map: GameMap, grid: HexGrid, fields: TerrainFields, atlas: Atlas): void {
    const trees = treeGlyphs(atlas);
    const peaks = peakGlyphs(atlas);
    type Item = { x: number; y: number; g: Glyph; s: number };
    const items: Item[] = [];
    const inland = (x: number, y: number, margin: number) => sampleField(fields, fields.sdf, x, y) > margin;
    const weight = (x: number, y: number, ch: number) => sampleField(fields, fields.weights, x, y, 4, ch);
    for (let h = 0; h < grid.count; h++) {
      const t = map.terrain[h];
      const cx = grid.cx(h);
      const cy = grid.cy(h);
      if (t === Terrain.Forest) {
        // 4×4 jittered sub-grid across the hex; the baked forest weight trims it to an organic edge
        for (let j = 0; j < 4; j++) {
          for (let i = 0; i < 4; i++) {
            const k = j * 4 + i;
            const x = cx + (i - 1.5 + (hash(h, k) - 0.5) * 0.9) * HEX_W * 0.26;
            const y = cy + (j - 1.5 + (hash(h, k + 50) - 0.5) * 0.9) * HEX_SIZE * 0.44;
            if (weight(x, y, 0) < 0.5 || !inland(x, y, 7) || map.cityAt[h] >= 0) continue;
            const g = trees[Math.floor(hash(h, k + 99) * trees.length)];
            items.push({ x, y, g, s: 0.8 + hash(h, k + 7) * 0.35 });
          }
        }
      } else if (t === Terrain.Mountains) {
        const n = 1 + Math.floor(hash(h, 1) * 2.2);
        for (let k = 0; k < n; k++) {
          const x = cx + (hash(h, k + 11) - 0.5) * HEX_W * 0.5;
          const y = cy + (hash(h, k + 23) - 0.5) * HEX_SIZE * 0.5 + 4;
          if (!inland(x, y, 12)) continue;
          const g = peaks[Math.floor(hash(h, k + 31) * peaks.length)];
          items.push({ x, y, g, s: 0.8 + hash(h, k + 41) * 0.45 });
        }
      }
    }
    items.sort((a, b) => a.y - b.y);
    const q = this.glyphs;
    for (const it of items) {
      // anchor at the glyph's foot so overlapping rows stack naturally
      q.push(
        it.x,
        it.y,
        0,
        (-it.g.h * it.s) / 2 + 2,
        it.g.w * it.s,
        it.g.h * it.s,
        it.g.frame,
        0xffffff,
        1,
        Scale.World,
      );
    }
    q.commit();
  }

  update(zoomCss: number, resolution: number): void {
    this.rivers.setZoom(zoomCss, resolution);
    this.roads.setZoom(zoomCss, resolution);
    this.rails.setZoom(zoomCss, resolution);
    // tree/peak glyphs dissolve into the shader's tint when they would be mere specks
    this.glyphs.mesh.alpha = Math.min(1, Math.max(0, (zoomCss - 0.38) / 0.3));
    this.rails.mesh.alpha = Math.min(1, Math.max(0.35, (zoomCss - 0.25) / 0.4));
  }

  destroy(): void {
    this.rivers.destroy();
    this.roads.destroy();
    this.rails.destroy();
    this.glyphs.destroy();
    this.container.destroy();
  }
}
